import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import { useGetMerchantInsightsQuery } from '../../store/modules/insights/api';
import { InsightsDashboard } from './components/InsightsDashboard';
import { Button } from '../../components/core/Button/Button';

// Wrapper component that resets when data changes
const MerchantInsightsWithData: React.FC<{
  insightsData: any;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}> = ({ insightsData, isLoading, isError, refetch }) => {
  const currentYearFromData = useMemo(
    () => insightsData?.years.find((year: any) => year.isCurrentPeriod)?.number,
    [insightsData],
  );

  // Use API's current period year if available, otherwise most recent year, otherwise calendar year
  const initialYear = useMemo(() => {
    if (currentYearFromData) return currentYearFromData;
    if (insightsData?.years?.length > 0) {
      // Sort years in descending order and pick the most recent
      const sortedYears = [...insightsData.years].sort((a, b) => b.number - a.number);
      return sortedYears[0].number;
    }
    return new Date().getFullYear();
  }, [currentYearFromData, insightsData?.years]);
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);

  const selectedYearData = useMemo(
    () => insightsData?.years.find((year: any) => year.number === selectedYear),
    [insightsData, selectedYear],
  );

  const handleRetry = () => {
    refetch();
  };

  if (isError) {
    return (
      <PageWrapper
        title="Merchant Insights"
        description="Key performance indicators for the business at a glance."
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            textAlign: 'center',
          }}
        >
          <Typography variant="large" fontWeight="semibold" gutterBottom>
            Unable to load merchant insights
          </Typography>
          <Typography variant="medium" color="text.secondary" sx={{ mb: 3 }}>
            Please try again or contact support if the problem persists.
          </Typography>
          <Button
            variant="contained"
            onClick={handleRetry}
            text="Retry"
            sx={{ px: 4, py: 1.5, minWidth: '120px' }}
          />
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      title="Merchant Insights"
      description="Key performance indicators for the business at a glance."
      isLoading={false}
      sx={{
        '& .MuiStack-root:first-of-type': {
          mb: 1,
        },
      }}
    >
      <InsightsDashboard
        data={selectedYearData}
        allYears={insightsData?.years}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        isLoading={isLoading}
        isParentMerchant={insightsData?.isParentMerchant}
      />
    </PageWrapper>
  );
};

export const MerchantInsights: React.FC = () => {
  const { merchantId } = useMerchant();

  const {
    data: insightsData,
    isLoading,
    isError,
    refetch,
  } = useGetMerchantInsightsQuery(
    { merchantId },
    {
      skip: !merchantId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Reset the component state when merchantId or data changes
  const resetKey = `${merchantId}-${insightsData?.years.length ?? 0}`;

  return (
    <MerchantInsightsWithData
      key={resetKey}
      insightsData={insightsData}
      isLoading={isLoading}
      isError={isError}
      refetch={refetch}
    />
  );
};

export default MerchantInsights;
